import { InfoCircleOutlined } from '@ant-design/icons'
import React from 'react'
import styled from 'styled-components'
import { Popover } from 'sutra/components/Popover/Popover'

import { darkBlueFontColor } from 'assets/styles/variables'

export interface TierConfig<T extends string = string> {
  key: T
  label: string
  color: string
  icon: (active: boolean) => React.ReactNode
}

export interface ElevationLevelPopoverProps<T extends string = string> {
  tiers: TierConfig<T>[]
  activeTierKey: T
  share: number | null | undefined
  /** Multiplier vs baseline (e.g. 1.5 = 50% higher). null/Infinity = "higher". undefined = omit. */
  comparisonMultiplier: number | null | undefined
}

function comparisonText(multiplier: number | null): React.ReactNode {
  if (multiplier === null || multiplier === Infinity)
    return (
      <>
        This is <strong>higher</strong> than pre-detection baseline levels.
      </>
    )
  const pct = (multiplier - 1) * 100
  if (pct > 0)
    return (
      <>
        This is <strong>{pct.toFixed(0)}% higher</strong> than pre-detection
        baseline levels.
      </>
    )
  if (pct < 0)
    return (
      <>
        This is <strong>{Math.abs(pct).toFixed(0)}% lower</strong> than
        pre-detection baseline levels.
      </>
    )
  return (
    <>
      This is <strong>the same as</strong> pre-detection baseline levels.
    </>
  )
}

export const ElevationLevelPopover = <T extends string>({
  tiers,
  activeTierKey,
  share,
  comparisonMultiplier,
}: ElevationLevelPopoverProps<T>): React.ReactElement => {
  const activeTier = tiers.find((t) => t.key === activeTierKey)
  const activeColor = activeTier?.color ?? darkBlueFontColor

  return (
    <ElevationPopover
      openOnHover
      nativeButton={false}
      initialFocus={false}
      content={
        <PopoverContent>
          <PopoverTitle>Elevation level</PopoverTitle>
          <PopoverDescription>
            Shows how elevated this issue is compared to pre-detection baseline
            levels.
          </PopoverDescription>
          <TierLevelsRow>
            {tiers.map((tier) => {
              const isActive = tier.key === activeTierKey
              return (
                <TierLevelItem
                  key={tier.key}
                  $active={isActive}
                  $color={tier.color}
                >
                  <TierLevelIcon>{tier.icon(isActive)}</TierLevelIcon>
                  <TierLevelLabel $active={isActive} $color={tier.color}>
                    {tier.label}
                  </TierLevelLabel>
                </TierLevelItem>
              )
            })}
          </TierLevelsRow>
          {(share != null || comparisonMultiplier !== undefined) && (
            <PopoverMetric>
              {share != null && (
                <>
                  <strong>{(share * 100).toFixed(1)}%</strong> of mentions in
                  this period.{' '}
                </>
              )}
              {comparisonMultiplier !== undefined &&
                comparisonText(comparisonMultiplier)}
            </PopoverMetric>
          )}
        </PopoverContent>
      }
    >
      <InfoIcon $color={activeColor}>
        <InfoCircleOutlined />
      </InfoIcon>
    </ElevationPopover>
  )
}

const PopoverContent = styled.div`
  padding: 4px 0;
`

const PopoverTitle = styled.div`
  font-weight: 600;
  font-size: 14px;
  color: ${darkBlueFontColor};
  margin-bottom: 6px;
`

const PopoverDescription = styled.p`
  font-size: 13px;
  color: ${darkBlueFontColor};
  line-height: 1.4;
  margin: 0 0 10px 0;
  opacity: 0.9;
`

const TierLevelsRow = styled.div`
  display: flex;
  align-items: stretch;
  justify-content: center;
  gap: 16px;
  margin-bottom: 10px;
`

const TierLevelItem = styled.div<{ $active: boolean; $color: string }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-end;
  flex: 1;
  gap: 4px;
  padding-bottom: 4px;
  border-bottom: 3px solid ${(p) => (p.$active ? p.$color : 'transparent')};
`

const TierLevelIcon = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 14px;
`

const TierLevelLabel = styled.span<{ $active: boolean; $color: string }>`
  font-size: 11px;
  font-weight: 600;
  letter-spacing: 0.02em;
  text-align: center;
  color: ${(p) => (p.$active ? p.$color : '#d9d9d9')};
`

const PopoverMetric = styled.p`
  font-size: 13px;
  color: ${darkBlueFontColor};
  line-height: 1.5;
  margin: 0;

  strong {
    font-weight: 600;
  }
`

const InfoIcon = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  cursor: help;
  color: ${(p) => p.$color};
  background-color: ${(p) => `${p.$color}1f`};
  font-size: 12px;
`

const ElevationPopover = styled(Popover)`
  max-width: 320px;
`
